// src/services/api.ts
//
// v0.4.9.3.5c2 — RESTORED the full pre-3.5 api object (the c1 rewrite
// accidentally replaced the whole file with just the civil-respondent
// slice, breaking GuardDashboard/POIFormModal/POIGalleryEditor).
//
// This version is api_pre_v0.4.9.3.5.ts + the correctPlate() addition.
// All Edge fallback helpers (edgeFetchWithFallback, buildEdgeImageSrc)
// are preserved. Do NOT delete them again.
//
// v0.4.6i — authFetch now sets the X-iSentinel-Client: 'apk' header when
// window.Android is defined.

import i18n from '../i18n';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.isentinel360.com';

function apkClientHeader(): Record<string, string> {
  if (typeof window !== 'undefined' && (window as any).Android) {
    return { 'X-iSentinel-Client': 'apk' };
  }
  return {};
}

function getAuthToken(): string | null {
  return localStorage.getItem('authToken');
}

/**
 * An error meaning "the session is not usable" — not "the request failed".
 *
 * Tagged so a caller can tell the two apart. This matters because a component
 * that renders a page-level error on ANY failure would otherwise paint
 * "Failed to load" for what is really a token rotation — and, since v0.4.9.4.x
 * made those throws benign, it would surface the raw message to the user.
 * Handle it: `if ((err as any)?.isAuthError) return;`
 */
function authError(message: string): Error {
  const e = new Error(message);
  (e as any).isAuthError = true;
  return e;
}

// How old (in ms) a freshly-issued token may be before a 401 is allowed to
// destroy it.
const TOKEN_GRACE_MS = 60_000;

/**
 * Age of the token currently in localStorage, from its own `iat` claim.
 * Returns null when the token is absent or unreadable.
 *
 * Decoded WITHOUT verifying the signature — that is authFetch's server-side
 * job. We only need to know "was this minted seconds ago?", and a token
 * already in localStorage is by definition one we issued to this client.
 */
function tokenAgeMs(jwt: string | null): number | null {
  if (!jwt) return null;
  const parts = jwt.split('.');
  if (parts.length < 2) return null;
  try {
    // atob is available in every browser and in the Android System WebView.
    // No Node Buffer here on purpose: referencing it breaks `tsc && vite build`.
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const iat = JSON.parse(atob(padded))?.iat;
    if (typeof iat !== 'number' || !isFinite(iat)) return null;
    return Date.now() - iat * 1000;
  } catch {
    return null;
  }
}

// v0.4.9.4.6 — `raw: true` returns the Response UNPARSED, so a caller that
// needs a Blob (the training-CSV download) or a stream can consume the body
// itself.
//
// Without it, authFetch's success path is:
//
//     const contentType = response.headers.get('content-type');
//     if (contentType && contentType.includes('application/json')) return response.json();
//     const text = await response.text();
//     return text ? JSON.parse(text) : { success: true };
//
// A `text/csv` response is not application/json, so it falls to the second
// line: the body is consumed with response.text() and then JSON.parse is
// called on a CSV string, which throws `Unexpected token 't' in JSON at
// position 0` on the first byte of `ts,action_id,…`. The download silently
// became an error toast.
//
// The declared return type is `Promise<any>` because that is what this
// function ALREADY returns: response.json() is `any`, JSON.parse is `any`,
// and the fallback is an object literal. Declaring it says so rather than
// changing behaviour. Every existing call site keeps its current path.
async function authFetch(
  endpoint: string,
  options: RequestInit & { raw?: boolean } = {},
  retriedAfterRotate = false
): Promise<any> {
  // `raw` is ours, not fetch's. It must not reach RequestInit.
  const { raw, ...fetchOptions } = options;
  const token = getAuthToken();
  if (!token) throw authError('No authentication token');
  const activeStoreId = localStorage.getItem('activeStoreId');

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    'Accept-Language': i18n.language || 'en',
    ...(activeStoreId ? { 'X-Store-Id': activeStoreId } : {}),
    ...apkClientHeader(),
    ...fetchOptions.headers,
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...fetchOptions,
    headers,
  });

  if (response.status === 401) {
    const current = getAuthToken();
    // The token this request was SENT with, vs the one in localStorage now.
    const tokenWasSuperseded = !!current && current !== token;

    if (!retriedAfterRotate && tokenWasSuperseded) {
      // `options`, not `fetchOptions` — the retry must carry `raw` too, or
      // a token rotation mid-download hands back a JSON.parse'd string
      // where the caller is waiting for a Response.
      return authFetch(endpoint, options, true);
    }

    // This 401 belongs to a request that was issued under a token which is no
    // longer current. The session it was issued under is gone; the session in
    // localStorage is NOT. Destroying it here is what logged a user out
    // seconds after a successful PassKey login: useBoloAlert fires a poll the
    // instant `user` changes (and every 10s), so a poll issued under the
    // previous session's token routinely lands 401 AFTER the new login has
    // stored its own. The old code saw a token present, concluded "session
    // terminated", and deleted the brand-new one. Second attempt wins because
    // no stale poll is in flight by then.
    //
    // Fail benignly instead: the caller sees an error, nothing is destroyed.
    if (tokenWasSuperseded) {
      throw authError('Request superseded by a newer session (401 on a retired token)');
    }

    // A token minted moments ago cannot plausibly have been terminated by the
    // server — the session behind it was created seconds ago, and a race with
    // an in-flight request or a replication gap is far more likely than a real
    // revocation. Treat it as retryable and leave the session intact.
    const age = tokenAgeMs(current);
    if (age !== null && age >= 0 && age < TOKEN_GRACE_MS) {
      console.warn(
        `[authFetch] 401 on a token that is only ${Math.round(age / 1000)}s old — ` +
        'treating as transient, session left intact'
      );
      throw authError(`Transient auth failure (401 on a ${Math.round(age / 1000)}s-old token)`);
    }

    const hasToken = !!localStorage.getItem('authToken');
    if (!hasToken) {
      localStorage.removeItem('currentUser');
      localStorage.removeItem('activeStoreId');
      localStorage.removeItem('userStores');
      if ((window as any).Android?.clearAuth) {
        try { (window as any).Android.clearAuth(); } catch { /* ignore */ }
      }
      if (window.location.pathname !== '/login') {
        window.location.href = '/login?reason=session-terminated';
      }
      throw authError('Not authenticated');
    }
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('activeStoreId');
    localStorage.removeItem('userStores');
    if ((window as any).Android?.clearAuth) {
      try { (window as any).Android.clearAuth(); } catch { /* ignore */ }
    }
    try {
      window.dispatchEvent(new CustomEvent('iSentinel360:session-kicked', {
        detail: { reason: 'logged_in_elsewhere' }
      }));
    } catch (_) { /* ignore */ }
    setTimeout(() => {
      if (window.location.pathname !== '/login' && !localStorage.getItem('authToken')) {
        window.location.href = '/login?reason=session-terminated';
      }
    }, 500);
    throw authError('Session terminated. Please login again.');
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  // v0.4.9.4.6: escape hatch — the caller wants the raw Response and will
  // consume the body itself (blob(), stream, etc.).
  if (raw) return response;

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  return text ? JSON.parse(text) : { success: true };
}

// ---------- POIs ----------
export interface POIFromAPI {
  id: string;
  code: string;
  threatLevel: 'High' | 'Medium' | 'Low';
  photoUrl: string;
  notes: string;
  lastSeen?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  accomplices?: AccompliceFromAPI[];
  vehicle?: VehicleFromAPI | null;
  editHistory?: EditHistoryEntry[];
  storeId?: string;
  storeName?: string | null;
  storeClientId?: string | null;
  isNetwork?: boolean;
  imageCount?: number;
  thumbnailUrl?: string | null;
}

export interface POIImage {
  id: string;
  photoUrl: string | null;
  createdAt: string;
  createdBy: string;
  justification: string;
  faceEmbedding?: number[];
}

export interface POICandidate {
  id: number;
  timestamp: string;
  role: string;
  location: string | null;
  camera_name: string;
  file_path: string;
  status: 'pending' | 'promoted' | 'discarded';
  poi_id: string | null;
}

export interface AccompliceFromAPI {
  id: string;
  code: string;
  threatLevel: 'High' | 'Medium' | 'Low';
  photoUrl: string;
  notes?: string;
  accomplicePoiId?: string;
}

export interface VehicleFromAPI {
  licensePlate: string;
  make?: string;
  model?: string;
  color?: string;
  location?: string;
  photoUrl?: string;
  poiDriverId?: string;
  driverCode?: string;
  driverPhotoUrl?: string;
}

export interface EditHistoryEntry {
  editedBy: string;
  editedByName?: string | null;
  editedAt: string;
  field: string;
  oldValue: any;
  newValue: any;
}

export interface POICreateData {
  code: string;
  threatLevel: 'High' | 'Medium' | 'Low';
  photoUrl: string;
  notes?: string;
  storeId?: string;
  accomplices?: Omit<AccompliceFromAPI, 'id'>[];
  vehicle?: VehicleFromAPI | null;
  photoFaceHash?: string | null;
  faceEmbedding?: number[];
}

export interface POIUpdateData {
  threatLevel?: 'High' | 'Medium' | 'Low';
  photoUrl?: string;
  notes?: string;
}

export interface StoreData {
  id: string;
  code: string;
  name: string;
  country_code: string;
  city_code: string;
  guardian_enabled: boolean;
  access_control_enabled: boolean;
  network_enabled: boolean;
  network_countries?: string[];
  max_guards: number;
  max_admins?: number;
  client_id?: string | null;
  created_at: string;
  admin_email?: string;
  edge_server_ip?: string;
  edge_server_port?: number;
  wifi_ssid?: string;
  hostname?: string | null;
  provisioning_status?: 'pending' | 'provisioned' | 'failed' | 'reset';
}

export interface ClientAlert {
  id: string;
  status: string;
  acknowledged_by: string | null;
  acknowledged_by_name?: string | null;
  store_name: string;
  store_id: string;
  event_type: string;
  created_at: string;
  capture_image_url?: string | null;
  poi?: {
    id: string;
    code: string;
    threatLevel: string;
    photoUrl: string;
    notes: string;
  } | null;
}

// v0.4.9.3.5c2: typed return shape for civil-respondent checks. Matches
// what checks.js returns and what ResultDisplay.tsx consumes.
export interface RunCivilCheckResult {
  match: any | null;
  score: number;
  target_type: 'missing_person' | 'most_wanted' | 'stolen_vehicle';
  action_id: string | null;
  ocr_plate: string | null;
  ocr_confidence: number | null;
  ocr_format_ok?: boolean | null;
  ocr_read_nothing?: boolean;
  can_edit?: boolean;
  image_kept?: boolean;
  correction_id?: string | null;
  snapshot_url?: string | null;
  message: string;
}

export interface CorrectPlateResult extends RunCivilCheckResult {
  correction_id: string | null;
  training_image_saved: boolean;
}

export const api = {
  // ----- v0.4.6i: auth session lifecycle -----
  logout: (): Promise<{ ok: boolean; killed: number }> =>
    authFetch('/api/auth/logout', { method: 'POST' }),

  resetUserDevice: (userId: string): Promise<{
    success: boolean;
    userId: string;
    apkInstalled: boolean;
    emailSent: boolean;
    killedSessions: number;
  }> =>
    authFetch(`/api/users/${userId}/reset-device`, { method: 'POST' }),

  refreshToken: (): Promise<{
    token: string;
    user: any;
    stores: Array<{ store_id: string; name: string }>;
    mustChangePassword?: boolean;
  }> =>
    authFetch('/api/auth/refresh', { method: 'POST' }),

  // POIs
  getPOIs: (storeId?: string): Promise<POIFromAPI[]> => {
    const headers: Record<string, string> = {};
    if (storeId) headers['X-Store-Id'] = storeId;
    return authFetch('/api/pois', { headers });
  },

  createPOI: (data: POICreateData): Promise<POIFromAPI> =>
    authFetch('/api/pois', { method: 'POST', body: JSON.stringify(data) }),

  updatePOI: (id: string, data: POIUpdateData): Promise<POIFromAPI> =>
    authFetch(`/api/pois/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  deletePOI: (id: string): Promise<{ success: boolean }> =>
    authFetch(`/api/pois/${id}`, { method: 'DELETE' }),

  getPoiImages: (poiId: string): Promise<{ images: POIImage[] }> =>
    authFetch(`/api/pois/${poiId}/images`),
  addPoiImage: (poiId: string, data: { photo_url: string; face_embedding: number[]; created_by: string; justification: string }): Promise<POIImage> =>
    authFetch(`/api/pois/${poiId}/images`, { method: 'POST', body: JSON.stringify(data) }),
  deletePoiImage: (poiId: string, imageId: string): Promise<{ success: boolean }> =>
    authFetch(`/api/pois/${poiId}/images/${imageId}`, { method: 'DELETE' }),

  // Alerts POIs
  getClientAlerts: (clientId?: string): Promise<ClientAlert[]> => {
    const query = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
    return authFetch(`/api/alerts/client${query}`);
  },

  getActiveAlerts: (storeId?: string): Promise<AlertFromAPI[]> => {
    const query = storeId ? `?storeId=${encodeURIComponent(storeId)}` : '';
    return authFetch(`/api/alerts/active${query}`);
  },

  getCompletedAlerts: (storeId?: string): Promise<AlertFromAPI[]> => {
    const query = storeId ? `?storeId=${encodeURIComponent(storeId)}` : '';
    return authFetch(`/api/alerts/completed${query}`);
  },

  createAlert: (poiId: string, storeId?: string): Promise<{ alertId: string }> =>
    authFetch('/api/alerts', { method: 'POST', body: JSON.stringify({ poiId, storeId }) }),

  acknowledgeAlert: (alertId: string): Promise<{ success: boolean }> =>
    authFetch(`/api/alerts/${alertId}/acknowledge`, { method: 'PATCH' }),

  completeAlert: (alertId: string, incidentReport: string): Promise<{ success: boolean }> =>
    authFetch(`/api/alerts/${alertId}/complete`, {
      method: 'PATCH',
      body: JSON.stringify({ incidentReport }),
    }),

  // ===================================================================
  // v0.4.9.2.2 — BOLO endpoints
  // ===================================================================

  listBolos: (
    orgId: string,
    opts: { target_type?: 'missing_person' | 'most_wanted' | 'stolen_vehicle'; status?: 'active' | 'cancelled' } = {}
  ): Promise<BoloFromAPI[]> => {
    const params = new URLSearchParams();
    if (opts.target_type) params.set('target_type', opts.target_type);
    if (opts.status)      params.set('status',      opts.status);
    const qs = params.toString();
    return authFetch(`/api/civil-respondents/${orgId}/bolos${qs ? `?${qs}` : ''}`);
  },

  getBolo: (boloId: string): Promise<BoloFromAPI> =>
    authFetch(`/api/bolos/${boloId}`),

  issueBolo: (
    orgId: string,
    body: { target_type: 'missing_person' | 'most_wanted' | 'stolen_vehicle'; target_id: string; scope: string; protocol: string }
  ): Promise<BoloFromAPI> =>
    authFetch(`/api/civil-respondents/${orgId}/bolos`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  reissueBolo: (boloId: string, notes?: string): Promise<{ success: boolean; issuance_count: number; recipient_count: number }> =>
    authFetch(`/api/bolos/${boloId}/reissue`, {
      method: 'POST',
      body: JSON.stringify({ notes: notes || null }),
    }),

  cancelBolo: (boloId: string, reason: string): Promise<{ success: boolean; cancelled_at: string }> =>
    authFetch(`/api/bolos/${boloId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  getActiveBolos: (): Promise<ActiveBoloFromAPI[]> =>
    authFetch('/api/civil-respondent/active-bolos'),

  getBoloHistory: (limit?: number): Promise<BoloHistoryFromAPI[]> => {
    const qs = limit ? `?limit=${limit}` : '';
    return authFetch(`/api/civil-respondent/bolo-history${qs}`);
  },

  acknowledgeBolo: (alertId: string, body?: { via?: 'web' | 'mobile' }): Promise<{
    acknowledged_at: string;
    acknowledged_via: string;
  }> =>
    authFetch(`/api/civil-alerts/${alertId}/acknowledge`, {
      method: 'POST',
      body: JSON.stringify(body || { via: 'web' }),
    }),

  listActiveBolos: (): Promise<any[]> =>
    authFetch('/api/civil-respondent/active-bolos'),

  listBoloHistory: (limit?: number): Promise<any[]> => {
    const qs = limit ? `?limit=${limit}` : '';
    return authFetch(`/api/civil-respondent/bolo-history${qs}`);
  },

  listAuthorizedPersonImages: (storeId: string, personId: string): Promise<{ person: { id: string; name: string }; images: { id: string; photo_url: string | null; created_at: string }[] }> =>
    authFetch(`/api/stores/${storeId}/authorized-persons/${personId}/images`),

  // display_photo_url is the tile a human sees in the gallery grid; photo_url
  // is the machine image the server re-embeds. The server has always accepted
  // both (stores.js) but nothing here could send the second one, so every
  // gallery tile showed the whole frame — often a piece of wall.
  addAuthorizedPersonImage: (storeId: string, personId: string, data: { photo_url: string; display_photo_url?: string; face_embedding: number[] }): Promise<{ id: string; person_id: string; photo_url: string | null; created_at: string }> =>
    authFetch(`/api/stores/${storeId}/authorized-persons/${personId}/images`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deleteAuthorizedPersonImage: (storeId: string, personId: string, imageId: string): Promise<{ success: boolean }> =>
    authFetch(`/api/stores/${storeId}/authorized-persons/${personId}/images/${imageId}`, { method: 'DELETE' }),

  simulateAccessAlert: (storeId: string, imageUrl: string): Promise<{ success: boolean }> =>
    authFetch(`/api/stores/${storeId}/simulate-access-alert`, {
      method: 'POST',
      body: JSON.stringify({ image_url: imageUrl }),
    }),

  getClients: () => authFetch('/api/clients'),
  createClient: (data: { name: string; address?: string; nif?: string; cadmin_email?: string; cadmin_password?: string }) =>
    authFetch('/api/clients', { method: 'POST', body: JSON.stringify(data) }),

  getClient: (clientId: string) => authFetch(`/api/clients/${clientId}`),
  updateClient: (clientId: string, data: any) =>
    authFetch(`/api/clients/${clientId}`, { method: 'PATCH', body: JSON.stringify(data) }),

  getStores: (clientId?: string): Promise<StoreData[]> => {
    const query = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
    return authFetch(`/api/stores${query}`);
  },

  getStore: (storeId: string): Promise<any> =>
    authFetch(`/api/stores/${storeId}`),

  createStore: (data: any): Promise<StoreData> =>
    authFetch('/api/stores', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateStore: (id: string, data: any): Promise<StoreData> =>
    authFetch(`/api/stores/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  getNextStoreNumber: (country: string, city: string): Promise<{ next_number: number; proposed_hostname: string }> =>
    authFetch(`/api/stores/next-number?country=${encodeURIComponent(country)}&city=${encodeURIComponent(city)}`),

  checkStoreNumber: (country: string, city: string, number: number): Promise<{ available: boolean; conflicting_store: any }> =>
    authFetch(`/api/stores/check-number?country=${encodeURIComponent(country)}&city=${encodeURIComponent(city)}&number=${number}`),

  provisionStore: (storeId: string): Promise<{
    store_id: string;
    hostname: string;
    setup_url: string;
    provisioning_token: string;
    expires_at: string;
    status: string;
  }> =>
    authFetch(`/api/stores/${storeId}/provision`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  resetStoreProvisioning: (storeId: string): Promise<{ success: boolean; status: string }> =>
    authFetch(`/api/stores/${storeId}/reset-provisioning`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  updateStoreUser: (storeId: string, userId: string, data: { name?: string; email?: string; role_in_store?: 'guard' | 'admin' | 'cadmin'; role?: 'guard' | 'admin' | 'cadmin' | 'sadmin' | 'superadmin' | 'tadmin'; training_enabled?: boolean }) =>
    authFetch(`/api/stores/${storeId}/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  getStoreUsers: (storeId: string): Promise<any[]> =>
    authFetch(`/api/stores/${storeId}/users`),

  searchUsers: async (q: string): Promise<Array<{ id: string; email: string; name: string | null; role: string; client_id: string | null }>> => {
    const query = q.trim();
    if (!query) return [];
    const res = (await authFetch(
      `/api/users/search?q=${encodeURIComponent(query)}`
    )) as { users?: any[] };
    return res?.users ?? [];
  },

  addStoreUser: (storeId: string, email: string, role_in_store: string, name?: string, training_enabled?: boolean, role?: string): Promise<{ success: boolean }> =>
    authFetch(`/api/stores/${storeId}/users`, {
      method: 'POST',
      body: JSON.stringify({ email, role_in_store, name, training_enabled, role }),
    }),

  removeStoreUser: (storeId: string, userId: string): Promise<{ success: boolean }> => {
    void userId;
    return authFetch(`/api/stores/${storeId}/users`, {
      method: 'DELETE',
    });
  },

  getStorePois: (storeId: string) => authFetch(`/api/pois/by-store/${storeId}`),

  uploadPhoto: (imageDataUrl: string): Promise<{ url: string }> =>
    authFetch('/api/upload', {
      method: 'POST',
      body: JSON.stringify({ image: imageDataUrl }),
    }),

  getAuthorizedPersons: (storeId: string): Promise<any[]> =>
    authFetch(`/api/stores/${storeId}/authorized-persons`),
  addAuthorizedPerson: (storeId: string, data: { name: string; photo_url?: string; notes?: string; role?: string; location?: string; face_embedding?: number[] }): Promise<any> =>
    authFetch(`/api/stores/${storeId}/authorized-persons`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  deleteAuthorizedPerson: (storeId: string, personId: string): Promise<{ success: boolean }> =>
    authFetch(`/api/stores/${storeId}/authorized-persons/${personId}`, { method: 'DELETE' }),

  updateAuthorizedPerson: (storeId: string, personId: string, data: { name?: string; photo_url?: string; notes?: string; role?: string; location?: string; face_embedding?: number[] }) =>
    authFetch(`/api/stores/${storeId}/authorized-persons/${personId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  getAuthorizedPlates: (storeId: string): Promise<any[]> =>
    authFetch(`/api/stores/${storeId}/authorized-plates`),
  addAuthorizedPlate: (storeId: string, data: { license_plate: string; make?: string; model?: string; color?: string; photo_url?: string; notes?: string }): Promise<any> =>
    authFetch(`/api/stores/${storeId}/authorized-plates`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  deleteAuthorizedPlate: (storeId: string, plateId: string): Promise<{ success: boolean }> =>
    authFetch(`/api/stores/${storeId}/authorized-plates/${plateId}`, { method: 'DELETE' }),

  updateAuthorizedPlate: (storeId: string, plateId: string, data: { license_plate?: string; make?: string; model?: string; color?: string; photo_url?: string; notes?: string }) =>
    authFetch(`/api/stores/${storeId}/authorized-plates/${plateId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  post: (endpoint: string, body: any, language?: string) =>
    authFetch(endpoint, {
      method: 'POST',
      headers: language ? { 'Accept-Language': language } : {},
      body: JSON.stringify(body),
    }),

  get: (endpoint: string) => authFetch(endpoint),

  updateStoreNetworkCountries: (storeId: string, network_countries: string[]): Promise<StoreData> =>
    authFetch(`/api/stores/${storeId}`, {
      method: 'PATCH',
      body: JSON.stringify({ network_countries }),
    }),

  getCameras: (storeId: string): Promise<any[]> =>
    authFetch(`/api/cameras?storeId=${encodeURIComponent(storeId)}`),
  createCamera: (data: {
    storeId: string;
    name: string;
    rtsp_url: string;
    role?: 'access_person' | 'poi_person' | 'poi_vehicle' | 'poi_mix' | 'access_vehicle' | 'drone_detection';
    location?: string;
    enabled?: boolean;
    sort_order?: number;
    poi_similarity_threshold?: number;
    poi_min_face_score?: number;
    poi_cooldown_sec?: number;
    ac_similarity_threshold?: number;
    ac_min_face_score?: number;
    ac_cooldown_sec?: number;
    min_bbox_ratio?: number;
    max_bbox_ratio?: number;
    capture_cooldown_sec?: number;
  }): Promise<any> =>
    authFetch('/api/cameras', { method: 'POST', body: JSON.stringify(data) }),
  updateCamera: (id: string, data: {
    name?: string;
    rtsp_url?: string;
    role?: 'access_person' | 'poi_person' | 'poi_vehicle' | 'poi_mix' | 'access_vehicle' | 'drone_detection';
    location?: string;
    enabled?: boolean;
    sort_order?: number;
    poi_similarity_threshold?: number;
    poi_min_face_score?: number;
    poi_cooldown_sec?: number;
    ac_similarity_threshold?: number;
    ac_min_face_score?: number;
    ac_cooldown_sec?: number;
    min_bbox_ratio?: number;
    max_bbox_ratio?: number;
    capture_cooldown_sec?: number;
  }): Promise<any> =>
    authFetch(`/api/cameras/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteCamera: (id: string): Promise<{ success: boolean }> =>
    authFetch(`/api/cameras/${id}`, { method: 'DELETE' }),

  checkDuplicate: async (photoHash?: string, faceEmbedding?: number[]): Promise<{ duplicate: boolean; existingCode?: string }> => {
    const body: any = {};
    if (photoHash) body.hash = photoHash;
    if (faceEmbedding) body.embedding = faceEmbedding;
    const data = await authFetch('/api/duplicates/check', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return { duplicate: data.duplicate, existingCode: data.existingCode };
  },

  checkAuthorizedPersonDuplicate: async (embedding: number[]): Promise<{ duplicate: boolean; existing?: { id: string; name: string; photoUrl: string } }> => {
    try {
      const result = await authFetch('/api/duplicates/check-authorized-embedding', {
        method: 'POST',
        body: JSON.stringify({ embedding }),
      });
      if (result.duplicate && result.existing) {
        return {
          duplicate: true,
          existing: {
            id: result.existing.id,
            name: result.existing.name || 'Unknown',
            photoUrl: result.existing.photo_url || '',
          }
        };
      }
      return { duplicate: false };
    } catch (err) {
      console.warn('[api] Authorized person duplicate check failed:', err);
      return { duplicate: false };
    }
  },

  getAllAlerts: (): Promise<AlertFromAPI[]> => authFetch('/api/alerts/all'),

  listCivilRespondents: (): Promise<any[]> => authFetch('/api/civil-respondents'),
  getCivilRespondent: (id: string): Promise<any> => authFetch(`/api/civil-respondents/${id}`),
  createCivilRespondent: (data: any): Promise<any> => authFetch('/api/civil-respondents', { method: 'POST', body: JSON.stringify(data) }),
  updateCivilRespondent: (id: string, data: any): Promise<any> => authFetch(`/api/civil-respondents/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteCivilRespondent: (id: string): Promise<{ success: boolean }> => authFetch(`/api/civil-respondents/${id}`, { method: 'DELETE' }),

  listCivilRespondentUsers: (orgId: string): Promise<any[]> => authFetch(`/api/civil-respondents/${orgId}/users`),
  createCivilRespondentUser: (orgId: string, data: { email: string; name?: string; role: 'agent' | 'agentAdmin' }): Promise<any> => authFetch(`/api/civil-respondents/${orgId}/users`, { method: 'POST', body: JSON.stringify(data) }),
  updateCivilRespondentUser: (orgId: string, userId: string, data: any): Promise<any> => authFetch(`/api/civil-respondents/${orgId}/users/${userId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteCivilRespondentUser: (orgId: string, userId: string): Promise<{ success: boolean }> => authFetch(`/api/civil-respondents/${orgId}/users/${userId}`, { method: 'DELETE' }),
  resetCivilRespondentUserPassword: (orgId: string, userId: string): Promise<{ success: boolean; emailSent: boolean }> => authFetch(`/api/civil-respondents/${orgId}/users/${userId}/reset-password`, { method: 'POST' }),

  listMissingPersons: (params?: { status?: string; country?: string }): Promise<any[]> => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.country) q.set('country', params.country);
    const qs = q.toString();
    return authFetch(`/api/missing-persons${qs ? '?' + qs : ''}`);
  },
  getMissingPerson: (id: string): Promise<any> => authFetch(`/api/missing-persons/${id}`),
  createMissingPerson: (data: any): Promise<any> => authFetch('/api/missing-persons', { method: 'POST', body: JSON.stringify(data) }),
  updateMissingPerson: (id: string, data: any): Promise<any> => authFetch(`/api/missing-persons/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteMissingPerson: (id: string): Promise<{ success: boolean }> => authFetch(`/api/missing-persons/${id}`, { method: 'DELETE' }),
  addMissingPersonFace: (id: string, image: string): Promise<any> => authFetch(`/api/missing-persons/${id}/faces`, { method: 'POST', body: JSON.stringify({ image }) }),
  listMissingPersonFaces: (id: string): Promise<any[]> => authFetch(`/api/missing-persons/${id}/faces`),
  deleteMissingPersonFace: (id: string, faceId: string): Promise<{ success: boolean }> => authFetch(`/api/missing-persons/${id}/faces/${faceId}`, { method: 'DELETE' }),

  listMostWanted: (params?: { status?: string; country?: string }): Promise<any[]> => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.country) q.set('country', params.country);
    const qs = q.toString();
    return authFetch(`/api/most-wanted${qs ? '?' + qs : ''}`);
  },
  getMostWanted: (id: string): Promise<any> => authFetch(`/api/most-wanted/${id}`),
  createMostWanted: (data: any, image?: string): Promise<any> => authFetch('/api/most-wanted', { method: 'POST', body: JSON.stringify({ ...data, image }) }),
  updateMostWanted: (id: string, data: any): Promise<any> => authFetch(`/api/most-wanted/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteMostWanted: (id: string): Promise<{ success: boolean; poi_soft_deleted?: boolean }> => authFetch(`/api/most-wanted/${id}`, { method: 'DELETE' }),
  listMostWantedFaces: (id: string): Promise<any[]> => authFetch(`/api/most-wanted/${id}/faces`),
  addMostWantedFace: (id: string, image: string): Promise<any> => authFetch(`/api/most-wanted/${id}/faces`, { method: 'POST', body: JSON.stringify({ image }) }),
  deleteMostWantedFace: (id: string, faceId: string): Promise<{ success: boolean }> => authFetch(`/api/most-wanted/${id}/faces/${faceId}`, { method: 'DELETE' }),

  listStolenVehicles: (params?: { status?: string; country?: string; q?: string }): Promise<any[]> => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.country) q.set('country', params.country);
    if (params?.q) q.set('q', params.q);
    const qs = q.toString();
    return authFetch(`/api/stolen-vehicles${qs ? '?' + qs : ''}`);
  },
  lookupStolenVehicleByPlate: (plate: string): Promise<any> => authFetch(`/api/stolen-vehicles/by-plate/${encodeURIComponent(plate)}`),
  getStolenVehicle: (id: string): Promise<any> => authFetch(`/api/stolen-vehicles/${id}`),
  createStolenVehicle: (data: any, image?: string): Promise<any> =>
    authFetch('/api/stolen-vehicles', { method: 'POST', body: JSON.stringify({ ...data, image }) }),
  updateStolenVehicle: (id: string, data: any, image?: string): Promise<any> =>
    authFetch(`/api/stolen-vehicles/${id}`, { method: 'PATCH', body: JSON.stringify({ ...data, image }) }),
  deleteStolenVehicle: (id: string): Promise<{ success: boolean }> => authFetch(`/api/stolen-vehicles/${id}`, { method: 'DELETE' }),
  bulkCreateStolenVehicles: (vehicles: any[]): Promise<{ inserted: number; failed: number; errors: any[] }> => authFetch('/api/stolen-vehicles/bulk', { method: 'POST', body: JSON.stringify({ vehicles }) }),

  // ----- Civil Respondent checks -----
  runCivilCheck: (image: string, targetType: 'missing_person' | 'most_wanted' | 'stolen_vehicle', organizationId?: string): Promise<RunCivilCheckResult> =>
    authFetch('/api/civil-respondent/check', { method: 'POST', body: JSON.stringify({ image, target_type: targetType, organization_id: organizationId }) }),
  listCivilActions: (): Promise<any[]> => authFetch('/api/civil-respondent/actions'),
  markActionFalsePositive: (id: string): Promise<{ success: boolean }> => authFetch(`/api/civil-respondent/actions/${id}/false-positive`, { method: 'POST' }),
  markActionFamilyContacted: (id: string, notes?: string): Promise<{ success: boolean }> => authFetch(`/api/civil-respondent/actions/${id}/family-contacted`, { method: 'POST', body: JSON.stringify({ notes }) }),
  markActionCompleted: (id: string, notes?: string): Promise<{ success: boolean }> => authFetch(`/api/civil-respondent/actions/${id}/action-completed`, { method: 'POST', body: JSON.stringify({ notes }) }),

  // v0.4.9.3.5: Edit / FP for plate checks.
  // The backend re-runs the lookup with the officer's value, promotes the
  // working copy to a training asset, and returns the new match info. On
  // success, the caller swaps the returned result into the display.
  //
  // `intent` values:
  //   'corrected'      OCR misread; `plate` is what the plate really says.
  //   'false_positive' a match was rejected; `plate` is what it really says.
  //   'manual'         officer typed the plate when OCR returned nothing.
  correctPlate: (actionId: string, plate: string, intent: 'corrected' | 'false_positive' | 'manual'): Promise<CorrectPlateResult> =>
    authFetch(`/api/civil-respondent/actions/${actionId}/plate-correction`, {
      method: 'POST',
      body: JSON.stringify({ plate, intent }),
    }),

  // ----- v0.4.9.4.6: LPR Setup (NetworkView "AI Training" tab) -----
  //
  // An admin (superadmin OR sadmin) tunes the plate OCR rules from the PWA:
  // regex patterns, country-code prefix list, scoring weights. The backend writes the JSON
  // file atomically; the Python detector hot-reloads on mtime change so
  // there's no Node restart and no plate-check downtime.
  lprGetRules: (): Promise<{ rules: any; mtime: number; path: string }> =>
    authFetch('/api/lpr/rules'),

  lprPutRules: (rules: any): Promise<{ success: boolean; rules: any; mtime: number; backup: string }> =>
    authFetch('/api/lpr/rules', {
      method: 'PUT',
      body: JSON.stringify({ rules }),
    }),

  lprGetRulesStats: (): Promise<{ path: string; mtime: number; size: number; has_backup: boolean; backup_mtime: number | null }> =>
    authFetch('/api/lpr/rules/stats'),

  // Force the Python loader to re-read the rules file. The endpoint bumps
  // the file's mtime, which is what the Python loader watches for. Returns
  // the new mtime so the UI can show "last reloaded at ...".
  lprReloadRules: (): Promise<{ success: boolean; mtime: number; message: string }> =>
    authFetch('/api/lpr/rules/reload', { method: 'POST' }),

  // v0.4.9.4.6 — CSV download of recent corrections + false positives +
  // OCR candidates, as a Blob.
  //
  // WHY NOT A PLAIN <a href download>:
  //   The route sits behind `authenticate`, which reads a Bearer token from
  //   the Authorization header. A bare <a href> is a browser navigation and
  //   cannot set headers, so the request went out unauthenticated and came
  //   back 401 — the button downloaded an error body named
  //   "lpr-training-<epoch>.csv" containing JSON, or navigated the PWA away
  //   entirely. It never worked, for superadmin and for sadmin alike; it was
  //   simply masked by the endpoint 500-ing first (column "ocr_format_ok"
  //   does not exist).
  //
  //   authFetch + Blob + object URL is the shape that actually carries the
  //   token. The old URL builder is kept only for previewing a link in a new
  //   tab and must NOT be used as a download link.
  //
  // @param since          ISO date string YYYY-MM-DD; defaults to last 30 days
  // @param organizationId REQUIRED for sadmin, optional for superadmin.
  //   The JWT carries no organization claim, so the server refuses an SAdmin
  //   export that does not name the org (fail-closed) rather than handing
  //   back every org's plate reads.
  lprDownloadTrainingCsv: async (since?: string, organizationId?: string): Promise<Blob> => {
    const params = new URLSearchParams();
    if (since) params.set('since', since);
    if (organizationId) params.set('organization_id', organizationId);
    const qs = params.toString();
    // `raw: true` is REQUIRED, not an optimisation. Without it authFetch
    // runs JSON.parse() on the CSV body and throws before returning, so the
    // download fails with "Unexpected token 't' in JSON at position 0".
    const res: Response = await authFetch(
      `/api/lpr/training-data.csv${qs ? `?${qs}` : ''}`,
      { raw: true, headers: { Accept: 'text/csv' } },
    );
    return res.blob();
  },

  // Triggers a browser download for a Blob. Kept here rather than in the
  // component so the UA-agnostic part is testable.
  saveBlob: (blob: Blob, filename: string): void => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoke on a later tick: revoking synchronously cancels the download
    // in some Android WebViews.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  // Preview-only URL. NOT a download link — see lprDownloadTrainingCsv.
  lprTrainingCsvUrl: (since?: string, organizationId?: string): string => {
    const params = new URLSearchParams();
    if (since) params.set('since', since);
    if (organizationId) params.set('organization_id', organizationId);
    const qs = params.toString();
    const base = `${API_BASE}/api/lpr/training-data.csv`;
    return qs ? `${base}?${qs}` : base;
  },

  // Returns a pre-formatted prompt an admin can paste into the LLM-friend
  // chat. Includes current rules + 30-day activity summary + 5 recent sample
  // rows + explicit output-shape instructions.
  lprGetLlmPrompt: (organizationId?: string): Promise<{ prompt: string; rules: any; stats: any }> =>
    authFetch(
      organizationId
        ? `/api/lpr/llm-prompt?organization_id=${encodeURIComponent(organizationId)}`
        : '/api/lpr/llm-prompt',
    ),

  opsecGetSnapshot: (): Promise<any> => authFetch('/api/admin/opsec/snapshot'),
  opsecGetBlockedIps: (): Promise<{ success: boolean; blocked: any[] }> => authFetch('/api/admin/opsec/blocked-ips'),
  opsecGetDenyIps: (): Promise<{ success: boolean; denied: string[] }> => authFetch('/api/admin/opsec/deny-ips'),
  opsecGetWhitelistedIps: (): Promise<{ success: boolean; whitelisted: any[]; count: number }> => authFetch('/api/admin/opsec/whitelisted-ips'),
  opsecGetBlockLog: (): Promise<{ success: boolean; log: string; count: number }> => authFetch('/api/admin/opsec/block-log'),
  opsecGetDenyLog: (): Promise<{ success: boolean; log: string; count: number }> => authFetch('/api/admin/opsec/deny-log'),
  opsecGetNginxDenyLog: (): Promise<{ success: boolean; log: string; path: string; count: number }> => authFetch('/api/admin/opsec/nginx-deny-log'),
  opsecBlockIp: (ip: string, reason?: string): Promise<{ success: boolean; message: string; ip: string }> =>
    authFetch('/api/admin/opsec/block-ip', { method: 'POST', body: JSON.stringify({ ip, reason }) }),
  opsecUnblockIp: (ip: string): Promise<{ success: boolean; message: string; ip: string }> =>
    authFetch('/api/admin/opsec/unblock-ip', { method: 'POST', body: JSON.stringify({ ip }) }),
  opsecDenyIp: (ip: string, reason?: string): Promise<{ success: boolean; message: string }> =>
    authFetch('/api/admin/opsec/deny-ip', { method: 'POST', body: JSON.stringify({ ip, reason }) }),
  opsecRemoveDenyIp: (ip: string): Promise<{ success: boolean; message: string }> =>
    authFetch('/api/admin/opsec/remove-deny-ip', { method: 'POST', body: JSON.stringify({ ip }) }),
  opsecWhitelistIp: (ip: string, reason?: string): Promise<{ success: boolean; message: string; ip: string; reason: string }> =>
    authFetch('/api/admin/opsec/whitelist-ip', { method: 'POST', body: JSON.stringify({ ip, reason }) }),
  opsecRemoveWhitelist: (ip: string): Promise<{ success: boolean; message: string }> =>
    authFetch('/api/admin/opsec/remove-whitelist', { method: 'POST', body: JSON.stringify({ ip }) }),

  opsecAbuseIpdbCheck: (ip: string): Promise<{
    success: boolean;
    cached?: boolean;
    ip?: string;
    abuseConfidenceScore?: number;
    totalReports?: number;
    countryCode?: string | null;
    isp?: string | null;
    usageType?: string | null;
    domain?: string | null;
    lastReportedAt?: string | null;
    dailyCount?: number;
    dailyLimit?: number;
    enabled?: boolean;
    error?: string;
  }> => authFetch(`/api/admin/opsec/abuseipdb-check?ip=${encodeURIComponent(ip)}`),

  opsecAbuseIpdbBulkBlock: (ips: string[], threshold: number = 100, source: 'AUTO' | 'MANUAL' = 'MANUAL'): Promise<{
    success: boolean;
    threshold: number;
    blocked: number;
    alreadyBlocked: number;
    results: any[];
  }> => authFetch('/api/admin/opsec/abuseipdb-bulk-block', {
    method: 'POST',
    body: JSON.stringify({ ips, threshold, source }),
  }),

  opsecReconInstantBlock: (ips: string[], reason?: string): Promise<{
    success: boolean;
    blocked: number;
    alreadyBlocked: number;
    results: any[];
  }> => authFetch('/api/admin/opsec/recon-instant-block', {
    method: 'POST',
    body: JSON.stringify({ ips, reason }),
  }),

  opsecVelocity: (): Promise<{
    generatedAt: string;
    windowMinutes: number;
    currentWindowMinutes: number;
    recentExcludedMinutes: number;
    minCurrent: number;
    minRatio: number;
    autoMinCurrent: number;
    ips: Array<{
      ip: string;
      currentRate: number;
      baselineRate: number;
      ratio: number;
      totalRequests: number;
      lastSeen: string | null;
      samplePath: string | null;
      whitelisted: boolean;
      isOwn: boolean;
      alreadyBlocked: boolean;
      suggestedAction: 'AUTO_BLOCK' | 'MANUAL' | 'WATCH';
      country: string | null;
      countryCode: string | null;
      city: string | null;
      isp: string | null;
      org: string | null;
    }>;
  }> => authFetch('/api/admin/opsec/velocity', { method: 'GET' }),

  opsecVelocityBlock: (ips: Array<string | { ip: string; currentRate?: number; ratio?: number }>, auto = false, reason?: string): Promise<{
    success: boolean;
    blocked: number;
    alreadyBlocked: number;
    results: any[];
  }> => authFetch('/api/admin/opsec/velocity-block', {
    method: 'POST',
    body: JSON.stringify({ ips, auto, reason }),
  }),

  opsecHistoricalAttacks: (params: {
    start?: string; end?: string; ip?: string; path?: string; limit?: number; maxLines?: number;
  } = {}): Promise<{
    success: boolean;
    range: { start: string; end: string };
    total: number;
    shown: number;
    limit: number;
    events: Array<{ ts: string; ip: string; path: string; status: string; reason: string }>;
    topIPs: Array<{ ip: string; count: number; country?: string; countryCode?: string; city?: string; isp?: string; org?: string }>;
    topPaths: Array<{ path: string; count: number }>;
    statusCounts: Record<string, number>;
    hourly: Array<{ hour: string; count: number }>;
    topCountries: Array<{ countryCode: string; country: string; count: number }>;
  }> => {
    const qs = new URLSearchParams();
    if (params.start) qs.set('start', params.start);
    if (params.end) qs.set('end', params.end);
    if (params.ip) qs.set('ip', params.ip);
    if (params.path) qs.set('path', params.path);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.maxLines) qs.set('maxLines', String(params.maxLines));
    return authFetch(`/api/admin/opsec/historical-attacks?${qs.toString()}`, { method: 'GET' });
  },

  opsecGeoip: (ip: string): Promise<{
    success: boolean;
    results: Array<{ ip: string; country: string; countryCode: string; city: string; isp: string; org: string; as?: string }>;
  }> => authFetch(`/api/admin/opsec/geoip?ip=${encodeURIComponent(ip)}`, { method: 'GET' }),

  opsecAutoBlockSummary: (minutes: number = 60): Promise<{
    success: boolean;
    minutes: number;
    autoCount: number;
    manualCount: number;
    reconCount: number;
    recentAutoIps: string[];
    recentReconIps: string[];
  }> => authFetch(`/api/admin/opsec/auto-block-summary?minutes=${minutes}`, {
    method: 'GET',
  }),
};

// =====================================================================
// v0.4.9.1.13+: Edge fallback helpers
// Restored in v0.4.9.2.2c1 — were missing from the user's current api.ts,
// causing MISSING_EXPORT errors in GuardDashboard.tsx, POIFormModal.tsx,
// POIGalleryEditor.tsx.
// =====================================================================

export interface EdgeEndpoint {
  hostname?: string | null;
  edgeIp?: string;
  edgePort?: number;
}

export async function edgeFetchWithFallback(
  opts: EdgeEndpoint,
  path: string,
  init: RequestInit = {}
): Promise<Response | null> {
  const hostname = opts.hostname && opts.hostname.trim() !== '' ? opts.hostname : null;
  const edgeIp = opts.edgeIp;
  const edgePort = opts.edgePort;
  const baseInit: RequestInit = {
    ...init,
    headers: { 'Content-Type': 'application/json', ...((init.headers as Record<string, string>) || {}) },
  };

  if (hostname) {
    try {
      const res = await fetch(`https://${hostname}${path}`, baseInit);
      if (res.ok) return res;
    } catch {
      // fall through
    }
  }

  if (edgeIp) {
    try {
      const res = await fetch(`https://${edgeIp}${path}`, baseInit);
      if (res.ok) return res;
    } catch {
      // fall through
    }
  }

  if (edgeIp && edgePort) {
    try {
      const res = await fetch(`http://${edgeIp}:${edgePort}${path}`, baseInit);
      if (res.ok) return res;
    } catch {
      // all three failed
    }
  }

  return null;
}

export function buildEdgeImageSrc(
  opts: EdgeEndpoint,
  path: string
): { primary: string; ipHttps: string | null; fallback: string | null } {
  const hostname = opts.hostname && opts.hostname.trim() !== '' ? opts.hostname : null;
  const edgeIp = opts.edgeIp;
  const edgePort = opts.edgePort;

  if (hostname) {
    return {
      primary: `https://${hostname}${path}`,
      ipHttps: edgeIp ? `https://${edgeIp}${path}` : null,
      fallback: edgeIp && edgePort ? `http://${edgeIp}:${edgePort}${path}` : null,
    };
  }

  if (edgeIp) {
    return {
      primary: `https://${edgeIp}${path}`,
      ipHttps: null,
      fallback: edgePort ? `http://${edgeIp}:${edgePort}${path}` : null,
    };
  }

  return { primary: '', ipHttps: null, fallback: null };
}

export interface AlertFromAPI {
  id: string;
  poi_id: string | null;
  store_id: string;
  created_at: string;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  acknowledgedByName?: string | null;
  completed_by: string | null;
  completed_at: string | null;
  incident_report: string | null;
  status: string;
  event_type: string;
  capture_image_url: string | null;
  poi?: POIFromAPI | null;
  createdAt: string;
  store_name?: string | null;
  camera_name?: string | null;
  note?: string | null;
  similarity?: number | null;
}

// =====================================================================
// v0.4.9.2.2 — BOLO interfaces
// =====================================================================

export type BoloTargetType = 'missing_person' | 'most_wanted' | 'stolen_vehicle';
export type BoloScope = 'network_wide' | 'le_country' | 'le_station';

export interface BoloFromAPI {
  id: string;
  country: string;
  country_code: string | null;
  target_type: BoloTargetType;
  target_id: string;
  scope: BoloScope;
  issuing_org_id: string;
  protocol: string;
  issued_by_user_id: string;
  issued_at: string;
  issuance_count: number;
  cancelled_at: string | null;
  cancelled_by_user_id: string | null;
  cancelled_reason: string | null;
  history?: BoloHistoryEntry[];
  issued_by_name?: string | null;
  recipient_count?: number;
  target_name?: string | null;
  target_photo_url?: string | null;
  target_description?: string | null;
}

export interface BoloHistoryEntry {
  id: string;
  bolo_id: string;
  event_type: 'issued' | 're_issued' | 'cancelled';
  event_at: string;
  actor_user_id: string;
  actor_name?: string | null;
  protocol_snapshot: string | null;
  scope_snapshot: string | null;
  notes: string | null;
}

export interface ActiveBoloFromAPI {
  id: string;
  country: string;
  country_code: string | null;
  target_type: BoloTargetType;
  target_id: string;
  scope: BoloScope;
  protocol: string;
  issued_at: string;
  issuance_count: number;
  issuing_org_id: string;
  cancelled_at: string | null;
  issued_by_name: string | null;
  issuing_org_name: string | null;
  alert_id: string | null;
  acknowledged_at: string | null;
  alert_created_at: string | null;
  alert_status: 'active' | 'acknowledged' | 'cancelled';
  target_name?: string | null;
  target_photo_url?: string | null;
  target_description?: string | null;
}

export interface BoloHistoryFromAPI {
  id: string;
  country: string;
  country_code: string | null;
  target_type: BoloTargetType;
  target_id: string;
  scope: BoloScope;
  protocol: string;
  issued_at: string;
  issuance_count: number;
  cancelled_at: string | null;
  cancelled_reason: string | null;
  issued_by_name: string | null;
  issuing_org_name: string | null;
  alert_id: string | null;
  alert_category: 'bolo' | 'bolo_cancelled' | null;
  acknowledged_at: string | null;
  alert_created_at: string;
  target_name?: string | null;
  target_photo_url?: string | null;
  target_description?: string | null;
}